import React, { useState, useEffect, useRef } from 'react';
import './Chat.css';
import { sendMessage, listenToMessages, getChatId, markChatAsReadLocal } from '../services/chatService';

const Chat = ({ 
  isOpen, 
  onClose, 
  requestId, 
  driverName, 
  passengerName, 
  currentUserName,
  description 
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingMessages, setPendingMessages] = useState(new Map()); // Map: messageText -> timestamp
  const messagesEndRef = useRef(null);
  const chatId = getChatId(requestId, driverName, passengerName);

  // Titre du chat avec conducteur et passager
  const chatTitle = `💬 Chat pour ${driverName} et ${passengerName}`;

  // Écouter les messages
  useEffect(() => {
    if (!isOpen || !chatId) return;

    // Ne pas marquer comme lu automatiquement - on le fera seulement à la fermeture

    const unsubscribe = listenToMessages(chatId, (newMessages) => {
      setMessages(newMessages);
      // Retirer les messages confirmés de la liste des messages en attente
      setPendingMessages(prev => {
        const updated = new Map(prev);
        newMessages.forEach(msg => {
          if (msg.senderName === currentUserName && msg.timestamp) {
            // Vérifier si ce message correspond à un message en attente
            // (en comparant le texte et en vérifiant que le timestamp est proche)
            const msgTime = msg.timestamp;
            for (const [pendingText, pendingTime] of updated.entries()) {
              if (pendingText === msg.message && Math.abs(msgTime - pendingTime) < 5000) {
                // Le message correspond et a été envoyé récemment
                updated.delete(pendingText);
                break;
              }
            }
          }
        });
        return updated;
      });
      // Scroll vers le bas après un court délai pour laisser le DOM se mettre à jour
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isOpen, chatId, currentUserName]);

  // Scroll automatique vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Envoyer un message
  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sending) return;

    const messageText = newMessage.trim();
    const sendTime = Date.now();
    setSending(true);
    
    // Ajouter le message à la liste des messages en attente avec son timestamp
    setPendingMessages(prev => new Map(prev).set(messageText, sendTime));
    
    const result = await sendMessage(chatId, currentUserName, messageText);
    
    if (result.success) {
      setNewMessage('');
      // Le message sera retiré de pendingMessages une fois qu'il apparaîtra dans le listener
    } else {
      // En cas d'erreur, retirer le message de la liste des messages en attente
      setPendingMessages(prev => {
        const updated = new Map(prev);
        updated.delete(messageText);
        return updated;
      });
      alert('Erreur lors de l\'envoi du message: ' + (result.error || 'Erreur inconnue'));
    }
    
    setSending(false);
  };

  // Formater l'heure d'un message
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  if (!isOpen) return null;

  return (
    <div className="chat-overlay" onClick={onClose}>
      <div className="chat-container" onClick={(e) => e.stopPropagation()}>
        <div className="chat-header">
          <h3>{chatTitle}</h3>
          <button className="chat-close-button" onClick={onClose} aria-label="Fermer le chat">
            ✕
          </button>
        </div>

        <div className="chat-messages">
          {description && (
            <div className="chat-description-banner">
              <div className="chat-description-icon">ℹ️</div>
              <div className="chat-description-content">
                <div className="chat-description-label">Description du passager :</div>
                <div className="chat-description-text">{description}</div>
              </div>
            </div>
          )}
          {messages.length === 0 ? (
            <div className="chat-empty">
              <p>Aucun message pour le moment.</p>
              <p className="chat-empty-hint">Soyez le premier à écrire ! 👋</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isOwnMessage = msg.senderName === currentUserName;
              const isPending = isOwnMessage && pendingMessages.has(msg.message);
              return (
                <div
                  key={msg.id}
                  className={`chat-message ${isOwnMessage ? 'chat-message-own' : 'chat-message-other'}`}
                >
                  <div className="chat-message-content">
                    <div className="chat-message-text">{msg.message}</div>
                    <div className="chat-message-footer">
                      <div className="chat-message-time">{formatTime(msg.timestamp)}</div>
                      {isOwnMessage && (
                        <div className="chat-message-status">
                          {isPending ? 'Envoi...' : 'Envoyé'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="chat-input-form" onSubmit={handleSend}>
          <input
            type="text"
            className="chat-input"
            placeholder="Tapez votre message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={sending}
            autoFocus
          />
          <button
            type="submit"
            className="chat-send-button"
            disabled={!newMessage.trim() || sending}
          >
            {sending ? '⏳' : '📤'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;
